import { Box, Chip, IconButton, Switch, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FlaskConical as ScienceIcon } from 'lucide-react';
import { useSimulation } from '@/hooks/useSimulation';
import { useHasPermission } from '@/components/PermissionGate';
import { useTranslation } from 'react-i18next';

/**
 * Toolbar toggle for simulation mode.
 * Flask icon acts as the SIM on/off toggle. When active, shows Drafts chip + toggle.
 */
export function SimulationToggle() {
  const { t } = useTranslation();
  const { isActive, includeDrafts, enable, disable, setIncludeDrafts } = useSimulation();
  const canSimulate = useHasPermission('Core.Simulation.Execute');
  const simColor = useTheme().palette.secondary.main;

  if (!canSimulate) return null;

  const handleToggle = () => {
    if (isActive) {
      disable();
    } else {
      enable(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {/* Drafts chip + toggle (only when active) */}
      {isActive && (
        <>
          <Chip
            label="Drafts"
            size="small"
            variant={includeDrafts ? 'filled' : 'outlined'}
            sx={{ height: 20, fontSize: '0.7rem', color: '#fff', borderColor: 'rgba(255,255,255,0.5)', ...(includeDrafts ? { bgcolor: simColor } : {}) }}
          />
          <Tooltip title={t('simulation.draftsTooltip')}>
            <Switch
              size="small"
              checked={includeDrafts}
              onChange={(_, checked) => setIncludeDrafts(checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: simColor },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: simColor },
              }}
            />
          </Tooltip>
          <Chip
            label="SIM"
            size="small"
            variant="filled"
            sx={{ height: 20, fontSize: '0.7rem', bgcolor: simColor, color: '#fff', ml: 0.5, mr: 0.5 }}
          />
        </>
      )}

      {/* Flask icon — always visible, click to toggle SIM mode */}
      <Tooltip title={t('simulation.tooltip')}>
        <IconButton size="small" onClick={handleToggle} sx={{ color: isActive ? simColor : 'inherit' }}>
          <ScienceIcon size={20} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
